import { z } from 'zod'

import type { AllGradeSubject, SchoolType } from '~/@entities/SchoolSystem'
import {
  formatWorksheetScoringGrade,
  type ScoringItemInput,
  toScoringItem,
} from '~/@entities/Scoring'
import type {
  CommonResponseAssignEchoResponse,
  CommonResponseListStudentWorksheetItemResponse,
  CommonResponseListStudentWorksheetVirtualScoringResponse,
  CommonResponseSliceGroupStudentWorksheetItemResponse,
  CommonResponseSliceGroupStudentWorksheetScoringListResponse,
  CommonResponseSliceStudentWorksheetItemResponse,
  CommonResponseSliceStudentWorksheetProblemItemResponse,
  CommonResponseStudentLearningWorksheetRelationShipResponse,
  CommonResponseStudentWorksheetItemResponse,
  CommonResponseWorksheetDetailByStudentResponse,
  CommonResponseWorksheetValidationResponse,
  StudentLearningWorksheetRelationShipResponse,
  StudentWorksheetItemResponse,
  StudentWorksheetVirtualScoringResponse,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'
import { type Slice, sliceSchema } from '~/schema/slice'

import {
  type AssignedWorksheet,
  type AssignedWorksheetDetail,
  AssignedWorksheetDetailSchema,
  type AssignedWorksheetProblemDetail,
  AssignedWorksheetProblemDetailSchema,
  type AssignedWorksheetProblemWithEssay,
  AssignedWorksheetProblemWithEssaySchema,
  AssignedWorksheetSchema,
  type AssignedWorksheetWithHomeworks,
  AssignedWorksheetWithHomeworksSchema,
  type ChallengeWorksheet,
  ChallengeWorksheetSchema,
  type GroupAssignedWorksheet,
  GroupAssignedWorksheetSchema,
  type GroupWorksheetAssignEcho,
  GroupWorksheetAssignEchoSchema,
  type GroupWorksheetScoring,
  GroupWorksheetScoringSchema,
  type SelfLearningWorksheetRelations,
  SelfLearningWorksheetRelationsWireSchema,
  type StudentWorksheetScoring,
  type StudentWorksheetVirtualScoring,
  StudentWorksheetVirtualScoringSchema,
  type WorksheetAssignEcho,
  WorksheetAssignEchoSchema,
  type WorksheetScoringProblem,
  type WorksheetValidation,
  WorksheetValidationSchema,
  type WorksheetWithProblems,
  WorksheetWithProblemsSchema,
} from './schemas'

const ENTITY = 'StudentWorksheet'

export const adaptAssignedWorksheet = (
  raw: CommonResponseStudentWorksheetItemResponse['data'],
): AssignedWorksheet => parseApiResponse(AssignedWorksheetSchema, raw, ENTITY)

// 학생-학습지 목록 계열은 컨트롤러가 StudentWorksheetItemResponse 로 선언하고 프레젠터가 그 하위클래스를
// 내서 스펙에 없는 필드가 실린다. 2026-09-29 백엔드 develop 의 응답 클래스로 확인.
//   1. `/student-worksheet/{studentId}/homeworks` — StudentWorksheetItemWithSelfLearningResponse.
//      `homeworks`·`selfLearningStudentWorksheets` 가 더 오고, 그 안의 worksheet 는
//      SelfLearningWorksheetResponse 라 `selfLearningType` 을 싣는다
//   2. `/student-learning/self-learning` — next 목록의 worksheet 는 기본 WorksheetResponse 라 selfLearningType 이 없다.
//      오답 자기주도 학습지에서만 조회되므로 adaptSelfLearningWorksheetRelations 가 WRONG 을 채운다
//   3. `/student-learning/self-learning/challenge/last` — StudentLearningChallengeWorksheetResponse.
//      `referenceType`·`referenceId`·`learningStep` 이 더 온다
//   4. `/student-worksheet/virtual-scoring` — virtualScores 원소를 스펙은 서술형 채점의 `Score` 로 그리지만
//      실제는 중첩 클래스 StudentWorksheetVirtualScoringResponse.Score(worksheetProblemId·result·
//      userAnswer·userAnswers)다. springdoc 이 동명의 중첩 클래스를 병합한 결함이다
//   5. `/student-worksheet/assign/{id}/problem` — 서술형 블록(`essay`)과 그 슬롯의 `label`·`answer` 는 Kotlin nullable 이라
//      키가 오고 값이 null 인데 스펙은 optional 로만 그린다(nullable 과소표기). 2026-10-07 백엔드 origin/develop(85d9b076a) 의
//      StudentWorksheetProblemItemResponse.kt·essayScoring/controller/response/EssayProblemResponse.kt 로 확인
//   6. 선생님 앱의 배정 기록·학년·반 목록 — 원클릭 보고서 공유 여부(`creverseShared`)·학생별 숙제 id(`studentHomeworkId`)·
//      서술형 공개 여부(`essayScoringOpened`)·목록 점수(`score`)·채점 현황의 학습지 id 와 문제 출처는 Kotlin nullable 이거나
//      기본값이 있는 필드라 키가 오고, 스펙은 optional 로만 그린다. 2026-10-14 백엔드 develop(6a0f8e7bb) 의
//      StudentWorksheetItemResponse.kt·GroupStudentWorksheetItemResponse.kt·AssignResponse.kt·
//      GroupStudentWorksheetScoringListResponse.kt 로 확인
// 스키마가 실제 모양을 검증하므로 입력은 생성 타입으로 받되 이 한 줄에서만 unknown 을 거친다.
const asWire = (
  raw:
    | StudentWorksheetItemResponse
    | StudentWorksheetItemResponse[]
    | StudentLearningWorksheetRelationShipResponse
    | StudentWorksheetVirtualScoringResponse[]
    | CommonResponseSliceStudentWorksheetProblemItemResponse['data']
    | CommonResponseSliceStudentWorksheetItemResponse['data']
    | CommonResponseSliceGroupStudentWorksheetItemResponse['data']
    | CommonResponseSliceGroupStudentWorksheetScoringListResponse['data']
    | CommonResponseAssignEchoResponse['data']
    | null
    | undefined,
): unknown => raw

export const adaptAssignedWorksheetsWithHomeworks = (
  raw: CommonResponseListStudentWorksheetItemResponse['data'],
): AssignedWorksheetWithHomeworks[] =>
  parseApiResponse(z.array(AssignedWorksheetWithHomeworksSchema), asWire(raw), ENTITY)

export const adaptSelfLearningWorksheetRelations = (
  raw: CommonResponseStudentLearningWorksheetRelationShipResponse['data'],
): SelfLearningWorksheetRelations => {
  const relations = parseApiResponse(SelfLearningWorksheetRelationsWireSchema, asWire(raw), ENTITY)
  return {
    ...relations,
    nextStudentWorksheetAndWorksheets: relations.nextStudentWorksheetAndWorksheets.map((item) => ({
      ...item,
      worksheet: { ...item.worksheet, selfLearningType: 'WRONG' },
    })),
  }
}

// 기존 챌린지 학습지가 없으면 서버가 data: null 로 답한다. 이것은 정상 응답이고 본문 부재(undefined)와 다르다.
/** 개념 학습 학습지 목록. 챌린지 학습지와 같은 모양(기준·단계를 더한 배정 기록)으로 온다 */
export const adaptChallengeWorksheets = (
  raw: CommonResponseListStudentWorksheetItemResponse['data'],
): ChallengeWorksheet[] => parseApiResponse(z.array(ChallengeWorksheetSchema), asWire(raw), ENTITY)

export const adaptChallengeWorksheet = (
  raw: CommonResponseStudentWorksheetItemResponse['data'] | null,
): ChallengeWorksheet | null =>
  parseApiResponse(ChallengeWorksheetSchema.nullable(), asWire(raw), ENTITY)

export const adaptWorksheetAssignEcho = (
  raw: CommonResponseAssignEchoResponse['data'],
): WorksheetAssignEcho => parseApiResponse(WorksheetAssignEchoSchema, raw, ENTITY)

export const adaptWorksheetValidation = (
  raw: CommonResponseWorksheetValidationResponse['data'],
): WorksheetValidation => parseApiResponse(WorksheetValidationSchema, raw, ENTITY)

export const adaptStudentWorksheetVirtualScorings = (
  raw: CommonResponseListStudentWorksheetVirtualScoringResponse['data'],
): StudentWorksheetVirtualScoring[] =>
  parseApiResponse(z.array(StudentWorksheetVirtualScoringSchema), asWire(raw), ENTITY)

/***************** 문제와 채점 *****************/

export const adaptAssignedWorksheetProblems = (
  raw: CommonResponseSliceStudentWorksheetProblemItemResponse['data'],
): AssignedWorksheetProblemWithEssay[] =>
  parseApiResponse(sliceSchema(AssignedWorksheetProblemWithEssaySchema), asWire(raw), ENTITY)
    .content

/***************** 선생님 앱의 출제 기록 *****************/

export const adaptAssignedWorksheetDetail = (
  raw: CommonResponseStudentWorksheetItemResponse['data'],
): AssignedWorksheetDetail => parseApiResponse(AssignedWorksheetDetailSchema, asWire(raw), ENTITY)

export const adaptAssignedWorksheetDetails = (
  raw: CommonResponseSliceStudentWorksheetItemResponse['data'],
): Slice<AssignedWorksheetDetail> =>
  parseApiResponse(sliceSchema(AssignedWorksheetDetailSchema), asWire(raw), ENTITY)

export const adaptGroupAssignedWorksheets = (
  raw: CommonResponseSliceGroupStudentWorksheetItemResponse['data'],
): Slice<GroupAssignedWorksheet> =>
  parseApiResponse(sliceSchema(GroupAssignedWorksheetSchema), asWire(raw), ENTITY)

export const adaptGroupWorksheetScorings = (
  raw: CommonResponseSliceGroupStudentWorksheetScoringListResponse['data'],
): Slice<GroupWorksheetScoring> =>
  parseApiResponse(sliceSchema(GroupWorksheetScoringSchema), asWire(raw), ENTITY)

export const adaptGroupWorksheetAssignEcho = (
  raw: CommonResponseAssignEchoResponse['data'],
): GroupWorksheetAssignEcho => parseApiResponse(GroupWorksheetAssignEchoSchema, asWire(raw), ENTITY)

export const adaptAssignedWorksheetProblemDetails = (
  raw: CommonResponseSliceStudentWorksheetProblemItemResponse['data'],
): AssignedWorksheetProblemDetail[] =>
  parseApiResponse(sliceSchema(AssignedWorksheetProblemDetailSchema), asWire(raw), ENTITY)
    ?.content ?? []

export const adaptWorksheetWithProblems = (
  raw: CommonResponseWorksheetDetailByStudentResponse['data'],
): WorksheetWithProblems => parseApiResponse(WorksheetWithProblemsSchema, raw, ENTITY)

export const toStudentWorksheetScoring = ({
  worksheet,
  problem,
  scoring,
}: {
  worksheet: {
    autoScorable: boolean
    label: string
    school?: SchoolType
    grade?: AllGradeSubject
  }
  problem: WorksheetScoringProblem
  scoring: ScoringItemInput<unknown, WorksheetScoringProblem>['record'] & {
    worksheetProblemId: StudentWorksheetScoring['id']
  }
}): StudentWorksheetScoring =>
  toScoringItem({
    target: { kind: 'WORKSHEET', id: scoring.worksheetProblemId } as const,
    problem,
    record: scoring,
    content: {
      label: worksheet.label,
      autoScorable: worksheet.autoScorable,
      schoolType: worksheet.school,
      gradeLabel: formatWorksheetScoringGrade(worksheet.school, worksheet.grade),
    },
    answerLength: problem.answerCount,
  })
